import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faBrush,
  faBullseye,
  faCircle,
  faDrawPolygon,
  faEraser,
  faEye,
  faEyeSlash,
  faHandPointer,
  faMousePointer,
  faSlash,
  faTags,
  faTimes,
  faVectorSquare,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { action } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { RowLayout } from '../../../../../../modules/common/layouts/rowLayout/RowLayout';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { IconButtonWithSlider } from '../../../../../components/IconButtonWithSlider';
import { ToolTypeIcon } from '../../../../../components/ToolTypeIcon';
import { BRUSH_MODE, IBrushTypeOptions } from '../../../annotations.interface';
import { AnnotationToolType } from '../models/annotationToolType';
import { IAnnotationType } from '../models/annotationType';
import { IAnnotationTypeOptions } from '../models/annotationTypeOptions';

library.add(faBullseye, faTags, faDrawPolygon, faEye, faEyeSlash, faVectorSquare, faHandPointer, faSlash, faMousePointer, faBrush, faEraser, faCircle, faTimes);

interface IProps extends IAnnotationType {
  projectId: string;
  active?: boolean;
  readonly: boolean;
  deleteSelected?: boolean;
  options?: IAnnotationTypeOptions;
  onAnnotationTypeClicked(id: string): Promise<void>;
  onAnnotationTypeOptionsChanged(projectId: string, annotationTypeOptions: IAnnotationTypeOptions): void;
  onSelectedAnnotationDelete: () => Promise<void>;
  getShortcutKey(order: number): string | undefined;
}

const DisplayShortcut = observer((props: IProps): ReactElement => {
  if (props.getShortcutKey) {
    const key = (): string | undefined => props.getShortcutKey(props.order);
    return <span className='shortcut'>{key()}</span>;
  }
  return <span className='shortcut' />;
});

export const AnnotationTypeListItem = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const [showModal, setShowModal] = useState(false);

  const handleRadiusChange = action((value: number): void => {
    if (props.options) {
      props.onAnnotationTypeOptionsChanged(props.projectId, { ...props.options, brushDiameter: value } as IBrushTypeOptions);
    }
  });

  const toggleModal = (): void => {
    setShowModal((prev) => !prev);
  };

  const handleOptionsChange = (options: IAnnotationTypeOptions): void => {
    props.onAnnotationTypeOptionsChanged(props.projectId, options);
  };

  return (
    <div className='annotation-types-single-item'>
      <div
        className={`annotation-tool ${props.active} ${props.readonly ? 'readonly' : ''}`}
        css={{ outlineColor: props.color }}
        onClick={(): void => {
          void props.onAnnotationTypeClicked(props.id);
        }}
      >
        <div
          className='rectangle'
          css={{ backgroundColor: props.color }}
        >
          <DisplayShortcut {...props} />
        </div>{' '}
        <ToolTypeIcon toolType={props.selectorType} />
        <div className='name'>
          <span title={props.name}>{props.name}</span>
        </div>
        <RowLayout
          size='sm'
          className='tool-options'
        >
          {props.active && props.options?.toolType === AnnotationToolType.BRUSH && (
            <>
              <IconButton
                color='primary'
                tabIndex={-1}
                size='sm'
                variant={(props.options as IBrushTypeOptions).brushMode === BRUSH_MODE.DRAW ? 'solid' : 'outlined'}
                title={t('options_brush')}
                onClick={(): void => {
                  handleOptionsChange({ ...props.options, brushMode: BRUSH_MODE.DRAW } as IBrushTypeOptions);
                }}
              >
                <FontAwesomeIcon icon={faBrush} />
              </IconButton>
              {(props.options as IBrushTypeOptions).eraserAvailable && (
                <IconButton
                  color='primary'
                  tabIndex={-1}
                  variant={(props.options as IBrushTypeOptions).brushMode === BRUSH_MODE.ERASE ? 'solid' : 'outlined'}
                  size='sm'
                  title={t('options_eraser')}
                  onClick={(): void => {
                    handleOptionsChange({ ...props.options, brushMode: BRUSH_MODE.ERASE } as IBrushTypeOptions);
                  }}
                >
                  <FontAwesomeIcon icon={faEraser} />
                </IconButton>
              )}
              <IconButtonWithSlider
                icon={faCircle}
                tabIndex={-1}
                title={t('options_radius')}
                minRange={1}
                maxRange={200}
                value={(props.options as IBrushTypeOptions).brushDiameter}
                onChange={handleRadiusChange}
                popupLabel='Size'
              />
            </>
          )}
          {props.deleteSelected && (
            <IconButton
              tabIndex={-1}
              variant='outlined'
              color='primary'
              size='sm'
              title={t('options_remove')}
              onClick={toggleModal}
            >
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          )}
          {props.options && (
            <>
              <IconButton
                tabIndex={-1}
                size='sm'
                color={props.options.isVisible ? 'primary' : 'neutral'}
                title={t(props.options.isVisible ? 'hide_all_annotations' : 'show_all_annotations')}
                onClick={(): void => {
                  handleOptionsChange({ ...props.options, isVisible: !props.options?.isVisible } as IBrushTypeOptions);
                }}
              >
                <FontAwesomeIcon icon={props.options.isVisible ? faEye : faEyeSlash} />
              </IconButton>
            </>
          )}
        </RowLayout>
      </div>

      <ConfirmationModal
        showModal={showModal}
        onConfirm={props.onSelectedAnnotationDelete}
        onCancel={toggleModal}
        confirmationHeader='delete_annotation_type_confirmation'
        confirmationQuestion='are_you_sure_you_want_to_delete_brush_marking'
        confirmationYes={'delete'}
      />
    </div>
  );
});
